import { useState, useRef, useMemo, useCallback, useEffect } from 'react';

/* ───────────────────── Types ───────────────────── */

export interface CellCoord {
  row: number;
  col: number; // numeric index into colIds
}

export interface SelectionRange {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
}

export interface CellSelectionState {
  activeCell: CellCoord | null;
  ranges: SelectionRange[];
  selectionEnd: CellCoord | null;
  toggledCells: Map<string, boolean>;
}

export interface CellBorders {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
}

/* ───────────────────── Helpers ───────────────────── */

const EMPTY_MAP: Map<string, boolean> = new Map();

const EMPTY_STATE: CellSelectionState = {
  activeCell: null,
  ranges: [],
  selectionEnd: null,
  toggledCells: EMPTY_MAP,
};

const ALL_BORDERS: CellBorders = { top: true, right: true, bottom: true, left: true };

function makeRange(r1: number, c1: number, r2: number, c2: number): SelectionRange {
  return { startRow: r1, startCol: c1, endRow: r2, endCol: c2 };
}

function cellKey(row: number, col: number): string {
  return `${row}:${col}`;
}

/* ───────────────────── Pre-computed selection cache ───────────────────── */

interface SelectionCache {
  cells: Set<string>;
  rows: Set<number>;
  cols: Set<number>;
  borders: Map<string, CellBorders>;
}

function buildSelectionCache(selection: CellSelectionState): SelectionCache {
  const cells = new Set<string>();
  const rows = new Set<number>();
  const cols = new Set<number>();
  const borders = new Map<string, CellBorders>();

  for (const range of selection.ranges) {
    const r0 = Math.min(range.startRow, range.endRow);
    const r1 = Math.max(range.startRow, range.endRow);
    const c0 = Math.min(range.startCol, range.endCol);
    const c1 = Math.max(range.startCol, range.endCol);
    const is1x1 = r0 === r1 && c0 === c1;

    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        const key = cellKey(r, c);
        // Toggled off — skip
        if (selection.toggledCells.get(key) === false) continue;

        cells.add(key);
        rows.add(r);
        cols.add(c);

        // Borders
        if (is1x1) {
          borders.set(key, ALL_BORDERS);
        } else {
          const top = r === r0 || selection.toggledCells.get(cellKey(r - 1, c)) === false;
          const bottom = r === r1 || selection.toggledCells.get(cellKey(r + 1, c)) === false;
          const left = c === c0 || selection.toggledCells.get(cellKey(r, c - 1)) === false;
          const right = c === c1 || selection.toggledCells.get(cellKey(r, c + 1)) === false;

          if (top && right && bottom && left) {
            borders.set(key, ALL_BORDERS);
          } else {
            const existing = borders.get(key);
            if (existing) {
              borders.set(key, {
                top: existing.top || top,
                right: existing.right || right,
                bottom: existing.bottom || bottom,
                left: existing.left || left,
              });
            } else {
              borders.set(key, { top, right, bottom, left });
            }
          }
        }
      }
    }
  }

  // Toggled-on cells not in any range
  for (const [k, v] of selection.toggledCells) {
    if (v && !cells.has(k)) {
      cells.add(k);
      const sepIdx = k.indexOf(':');
      rows.add(parseInt(k.slice(0, sepIdx), 10));
      cols.add(parseInt(k.slice(sepIdx + 1), 10));
      borders.set(k, ALL_BORDERS);
    }
  }

  return { cells, rows, cols, borders };
}

/* ───────────────────── Hook params & return ───────────────────── */

export interface UseCellSelectionParams {
  colIds: string[];
  rowCountRef: React.MutableRefObject<number>;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  rowHeight: number;
  headerHeight?: number;
  tableSelector: string;
  onCopy?: () => void;
  onCopyWithHeaders?: () => void;
}

export interface UseCellSelectionReturn {
  selection: CellSelectionState;
  isCellSelected: (row: number, col: number) => boolean;
  isCellActive: (row: number, col: number) => boolean;
  isRowSelected: (row: number) => boolean;
  isColSelected: (col: number) => boolean;
  getCellBorders: (row: number, col: number) => CellBorders | null;
  colIdToIndex: Map<string, number>;
  clearSelection: () => void;
  selectColumn: (colIndex: number, additive: boolean) => void;
  deselectColumn: (colIndex: number) => void;
  isColumnFullySelected: (colIndex: number) => boolean;
  handleCellMouseDown: (e: React.MouseEvent, columnId: string, rowIndex: number) => void;
  handleCellMouseEnter: (e: React.MouseEvent, columnId: string, rowIndex: number) => void;
  handleCellMouseUp: () => void;
  ensureCellSelectedForContextMenu: (columnId: string, rowIndex: number) => void;
  getSelectedCellsAsSet: () => Set<string>;
  getSelectedCellCount: () => number;
}

/* ───────────────────── Hook ───────────────────── */

export function useCellSelection({
  colIds,
  rowCountRef,
  scrollContainerRef,
  rowHeight,
  headerHeight = 40,
  tableSelector,
  onCopy,
  onCopyWithHeaders,
}: UseCellSelectionParams): UseCellSelectionReturn {

  const [selection, setSelection] = useState<CellSelectionState>(EMPTY_STATE);

  // Refs for drag tracking (not in state to avoid re-renders during drag)
  const isDraggingRef = useRef(false);
  const dragAnchorRef = useRef<CellCoord | null>(null);
  const dragHasCtrlRef = useRef(false);
  const preDragRangesRef = useRef<SelectionRange[]>([]);
  const preDragTogglesRef = useRef<Map<string, boolean>>(EMPTY_MAP);
  const rowDragRef = useRef(false);
  const tableFocusedRef = useRef(false);
  const onCopyRef = useRef(onCopy);
  onCopyRef.current = onCopy;
  const onCopyWithHeadersRef = useRef(onCopyWithHeaders);
  onCopyWithHeadersRef.current = onCopyWithHeaders;

  // Ref mirrors for side-effect-free access in stable callbacks
  const activeCellRef = useRef<CellCoord | null>(null);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  // RAF throttle for drag
  const dragRafRef = useRef(0);
  const pendingDragRef = useRef<{ row: number; col: number } | null>(null);
  const lastMouseRef = useRef<{ clientX: number; clientY: number } | null>(null);

  // colId → numeric index mapping
  const colIdToIndex = useMemo(
    () => new Map(colIds.map((id, i) => [id, i])),
    [colIds],
  );
  const colIdToIndexRef = useRef(colIdToIndex);
  colIdToIndexRef.current = colIdToIndex;
  const colIdsRef = useRef(colIds);
  colIdsRef.current = colIds;

  // Clear selection when colIds change (pin/reorder/L2CP toggle)
  const prevColIdsRef = useRef(colIds);
  useEffect(() => {
    if (prevColIdsRef.current !== colIds) {
      prevColIdsRef.current = colIds;
      setSelection(EMPTY_STATE);
    }
  }, [colIds]);

  /* ── Pre-computed cache: O(1) lookups instead of O(ranges) per cell ── */

  const cache = useMemo(() => buildSelectionCache(selection), [selection]);
  activeCellRef.current = selection.activeCell;

  // Store cache in a ref so query callbacks can have stable identity (empty deps).
  // This makes React.memo on table components effective — callbacks don't change
  // on every selection update, only the data they read from the ref does.
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  /* ── Query functions (O(1) via cache, stable identity via ref) ── */

  const isCellSelected = useCallback(
    (row: number, col: number): boolean => cacheRef.current.cells.has(cellKey(row, col)),
    [],
  );

  const isCellActive = useCallback(
    (row: number, col: number): boolean => {
      const a = activeCellRef.current;
      return a !== null && a.row === row && a.col === col;
    },
    [],
  );

  const isRowSelected = useCallback(
    (row: number): boolean => cacheRef.current.rows.has(row),
    [],
  );

  const isColSelected = useCallback(
    (col: number): boolean => cacheRef.current.cols.has(col),
    [],
  );

  const getCellBorders = useCallback(
    (row: number, col: number): CellBorders | null =>
      cacheRef.current.borders.get(cellKey(row, col)) ?? null,
    [],
  );

  const clearSelection = useCallback(() => {
    setSelection((prev) =>
      prev.ranges.length === 0 && prev.activeCell === null && prev.toggledCells.size === 0
        ? prev
        : EMPTY_STATE,
    );
  }, []);

  /* ── Column / row selection ── */

  const selectColumn = useCallback(
    (colIndex: number, additive: boolean) => {
      if (rowCountRef.current <= 0) return;
      tableFocusedRef.current = true;
      const range = makeRange(0, colIndex, rowCountRef.current - 1, colIndex);
      if (additive) {
        setSelection((prev) => ({
          ...prev,
          activeCell: { row: 0, col: colIndex },
          ranges: [...prev.ranges, range],
          selectionEnd: null,
          toggledCells: EMPTY_MAP,
        }));
      } else {
        setSelection({
          activeCell: { row: 0, col: colIndex },
          ranges: [range],
          selectionEnd: null,
          toggledCells: EMPTY_MAP,
        });
      }
    },
    [rowCountRef],
  );

  const deselectColumn = useCallback(
    (colIndex: number) => {
      if (rowCountRef.current <= 0) return;
      tableFocusedRef.current = true;
      setSelection((prev) => {
        const rc = rowCountRef.current - 1;
        const newRanges = prev.ranges.filter(
          (r) =>
            !(
              Math.min(r.startCol, r.endCol) === colIndex &&
              Math.max(r.startCol, r.endCol) === colIndex &&
              Math.min(r.startRow, r.endRow) === 0 &&
              Math.max(r.startRow, r.endRow) === rc
            ),
        );
        if (newRanges.length === prev.ranges.length) return prev;
        const newActive =
          prev.activeCell?.col === colIndex
            ? newRanges.length > 0
              ? { row: newRanges[newRanges.length - 1].startRow, col: newRanges[newRanges.length - 1].startCol }
              : null
            : prev.activeCell;
        return {
          ...prev,
          ranges: newRanges,
          activeCell: newActive,
          selectionEnd: null,
          toggledCells: EMPTY_MAP,
        };
      });
    },
    [rowCountRef],
  );

  const isColumnFullySelected = useCallback(
    (colIndex: number): boolean => {
      if (rowCountRef.current <= 0) return false;
      const rc = rowCountRef.current - 1;
      return selectionRef.current.ranges.some(
        (r) =>
          Math.min(r.startCol, r.endCol) === colIndex &&
          Math.max(r.startCol, r.endCol) === colIndex &&
          Math.min(r.startRow, r.endRow) === 0 &&
          Math.max(r.startRow, r.endRow) === rc,
      );
    },
    [rowCountRef],
  );

  /* ── Mouse handlers ── */

  const handleCellMouseDown = useCallback(
    (e: React.MouseEvent, columnId: string, rowIndex: number) => {
      // Only handle left-click (0). Skip middle-click (1, scroll) and right-click (2, context menu).
      if (e.button !== 0) return;

      const isCtrl = e.ctrlKey || e.metaKey;
      const isShift = e.shiftKey;
      const ids = colIdsRef.current;

      // Row number column — special row selection
      if (columnId === 'rowNumber') {
        e.preventDefault();
        const fullRowRange = makeRange(rowIndex, 0, rowIndex, ids.length - 1);

        if (isCtrl) {
          // Ctrl + row number: add/toggle entire row
          setSelection((prev) => {
            // Check if entire row is already selected via cache.
            // O(cols × ranges): fine for current tables (tens of cols, few
            // ranges). If column count grows to hundreds, build a prev-scoped
            // cache here (same shape as buildSelectionCache) to get O(cells).
            const allSelected = ids.every((_, ci) => {
              const key = cellKey(rowIndex, ci);
              if (prev.toggledCells.has(key)) return prev.toggledCells.get(key)!;
              // Inline range check (cache may be stale inside updater)
              for (const r of prev.ranges) {
                const r0 = Math.min(r.startRow, r.endRow);
                const r1 = Math.max(r.startRow, r.endRow);
                const c0 = Math.min(r.startCol, r.endCol);
                const c1 = Math.max(r.startCol, r.endCol);
                if (rowIndex >= r0 && rowIndex <= r1 && ci >= c0 && ci <= c1) return true;
              }
              return false;
            });
            if (allSelected) {
              // Deselect: try to remove exact row ranges, else toggle
              const newRanges = prev.ranges.filter(
                (r) =>
                  !(
                    r.startRow === rowIndex &&
                    r.endRow === rowIndex &&
                    r.startCol === 0 &&
                    r.endCol === ids.length - 1
                  ),
              );
              if (newRanges.length < prev.ranges.length) {
                return {
                  ...prev,
                  ranges: newRanges,
                  activeCell:
                    prev.activeCell?.row === rowIndex
                      ? newRanges.length > 0
                        ? { row: newRanges[newRanges.length - 1].startRow, col: newRanges[newRanges.length - 1].startCol }
                        : null
                      : prev.activeCell,
                  selectionEnd: null,
                  toggledCells: EMPTY_MAP,
                };
              }
              // Row is in a larger range — toggle each cell off
              const newToggles = new Map(prev.toggledCells);
              for (let ci = 0; ci < ids.length; ci++) {
                newToggles.set(cellKey(rowIndex, ci), false);
              }
              return {
                ...prev,
                activeCell:
                  prev.activeCell?.row === rowIndex
                    ? prev.ranges.length > 0
                      ? { row: prev.ranges[prev.ranges.length - 1].startRow, col: prev.ranges[prev.ranges.length - 1].startCol }
                      : null
                    : prev.activeCell,
                toggledCells: newToggles,
                selectionEnd: null,
              };
            }
            // Add row
            return {
              ...prev,
              activeCell: { row: rowIndex, col: 0 },
              ranges: [...prev.ranges, fullRowRange],
              selectionEnd: null,
            };
          });
        } else if (isShift) {
          // Shift + row number: range of rows from active row
          setSelection((prev) => {
            if (!prev.activeCell) {
              return {
                activeCell: { row: rowIndex, col: 0 },
                ranges: [fullRowRange],
                selectionEnd: null,
                toggledCells: EMPTY_MAP,
              };
            }
            const rangeRows = makeRange(prev.activeCell.row, 0, rowIndex, ids.length - 1);
            const newRanges = prev.ranges.length > 0 ? [...prev.ranges.slice(0, -1), rangeRows] : [rangeRows];
            return {
              ...prev,
              ranges: newRanges,
              selectionEnd: null,
              toggledCells: EMPTY_MAP,
            };
          });
        } else {
          // Plain click on row number — start row drag
          isDraggingRef.current = true;
          rowDragRef.current = true;
          dragAnchorRef.current = { row: rowIndex, col: 0 };
          dragHasCtrlRef.current = false;
          preDragRangesRef.current = [];
          preDragTogglesRef.current = EMPTY_MAP;
          setSelection({
            activeCell: { row: rowIndex, col: 0 },
            ranges: [fullRowRange],
            selectionEnd: null,
            toggledCells: EMPTY_MAP,
          });
        }
        return;
      }

      // Regular cell
      const ci = colIdToIndexRef.current.get(columnId);
      if (ci === undefined) return;
      e.preventDefault();

      if (isShift) {
        // Shift+Click: range from activeCell to clicked
        setSelection((prev) => {
          if (!prev.activeCell) {
            return {
              activeCell: { row: rowIndex, col: ci },
              ranges: [makeRange(rowIndex, ci, rowIndex, ci)],
              selectionEnd: null,
              toggledCells: EMPTY_MAP,
            };
          }
          const newRange = makeRange(prev.activeCell.row, prev.activeCell.col, rowIndex, ci);
          const newRanges = prev.ranges.length > 0 ? [...prev.ranges.slice(0, -1), newRange] : [newRange];
          return {
            ...prev,
            ranges: newRanges,
            selectionEnd: null,
            toggledCells: EMPTY_MAP,
          };
        });
        return;
      }

      if (isCtrl) {
        // Ctrl+Click: add or toggle
        const coord: CellCoord = { row: rowIndex, col: ci };
        dragAnchorRef.current = coord;
        dragHasCtrlRef.current = true;
        isDraggingRef.current = true;

        setSelection((prev) => {
          preDragRangesRef.current = prev.ranges;
          preDragTogglesRef.current = prev.toggledCells;

          const key = cellKey(rowIndex, ci);
          const isSelected = prev.toggledCells.has(key)
            ? prev.toggledCells.get(key)!
            : (() => {
                for (const r of prev.ranges) {
                  const r0 = Math.min(r.startRow, r.endRow);
                  const r1 = Math.max(r.startRow, r.endRow);
                  const c0 = Math.min(r.startCol, r.endCol);
                  const c1 = Math.max(r.startCol, r.endCol);
                  if (rowIndex >= r0 && rowIndex <= r1 && ci >= c0 && ci <= c1) return true;
                }
                return false;
              })();

          if (isSelected) {
            // Toggle off: try to remove exact 1×1 range first
            const exactIdx = prev.ranges.findIndex(
              (r) =>
                r.startRow === rowIndex &&
                r.endRow === rowIndex &&
                r.startCol === ci &&
                r.endCol === ci,
            );
            if (exactIdx !== -1) {
              const newRanges = [...prev.ranges];
              newRanges.splice(exactIdx, 1);
              // Update preDrag refs after removal
              preDragRangesRef.current = newRanges;
              const newActive =
                prev.activeCell?.row === rowIndex && prev.activeCell?.col === ci
                  ? newRanges.length > 0
                    ? { row: newRanges[newRanges.length - 1].startRow, col: newRanges[newRanges.length - 1].startCol }
                    : null
                  : prev.activeCell;
              return {
                ...prev,
                ranges: newRanges,
                activeCell: newActive,
                selectionEnd: null,
              };
            }
            // Cell is inside a larger range — use toggle
            const newToggles = new Map(prev.toggledCells);
            newToggles.set(key, false);
            // Propagate toggle-off into preDrag refs so a subsequent drag (from
            // micro mouse movement or intentional) doesn't discard it.
            preDragTogglesRef.current = newToggles;
            const newActive =
              prev.activeCell?.row === rowIndex && prev.activeCell?.col === ci
                ? prev.ranges.length > 0
                  ? { row: prev.ranges[prev.ranges.length - 1].startRow, col: prev.ranges[prev.ranges.length - 1].startCol }
                  : null
                : prev.activeCell;
            return {
              ...prev,
              activeCell: newActive,
              toggledCells: newToggles,
              selectionEnd: null,
            };
          }

          // Add cell
          const newRange = makeRange(rowIndex, ci, rowIndex, ci);
          const newRanges = [...prev.ranges, newRange];
          preDragRangesRef.current = newRanges;
          return {
            ...prev,
            activeCell: coord,
            ranges: newRanges,
            selectionEnd: null,
          };
        });
        return;
      }

      // Plain click: clear all, select one cell, start drag
      const coord: CellCoord = { row: rowIndex, col: ci };
      dragAnchorRef.current = coord;
      dragHasCtrlRef.current = false;
      isDraggingRef.current = true;
      preDragRangesRef.current = [];
      preDragTogglesRef.current = EMPTY_MAP;

      setSelection({
        activeCell: coord,
        ranges: [makeRange(rowIndex, ci, rowIndex, ci)],
        selectionEnd: null,
        toggledCells: EMPTY_MAP,
      });
    },
    [],
  );

  const flushDragUpdate = useCallback(() => {
    dragRafRef.current = 0;
    const target = pendingDragRef.current;
    const anchor = dragAnchorRef.current;
    if (!target || !anchor) return;

    const newRange = makeRange(anchor.row, anchor.col, target.row, target.col);

    if (dragHasCtrlRef.current) {
      const preToggles = preDragTogglesRef.current;
      setSelection((prev) => {
        // Skip no-op: check if last range already matches
        const lastRange = prev.ranges[prev.ranges.length - 1];
        if (
          lastRange &&
          prev.ranges.length === preDragRangesRef.current.length + 1 &&
          lastRange.startRow === newRange.startRow &&
          lastRange.endRow === newRange.endRow &&
          lastRange.startCol === newRange.startCol &&
          lastRange.endCol === newRange.endCol
        ) {
          return prev;
        }
        return {
          ...prev,
          activeCell: anchor,
          ranges: [...preDragRangesRef.current, newRange],
          toggledCells: preToggles,
          selectionEnd: null,
        };
      });
    } else {
      setSelection((prev) => {
        // Skip no-op
        const lastRange = prev.ranges[0];
        if (
          prev.ranges.length === 1 &&
          lastRange &&
          lastRange.startRow === newRange.startRow &&
          lastRange.endRow === newRange.endRow &&
          lastRange.startCol === newRange.startCol &&
          lastRange.endCol === newRange.endCol
        ) {
          return prev;
        }
        return {
          activeCell: anchor,
          ranges: [newRange],
          selectionEnd: null,
          toggledCells: EMPTY_MAP,
        };
      });
    }
  }, []);

  const handleCellMouseEnter = useCallback(
    (_e: React.MouseEvent, columnId: string, rowIndex: number) => {
      if (!isDraggingRef.current) return;

      if (rowDragRef.current) {
        // Row drag mode: extend to full rows regardless of which column cursor is over
        pendingDragRef.current = { row: rowIndex, col: colIdsRef.current.length - 1 };
        if (!dragRafRef.current) {
          dragRafRef.current = requestAnimationFrame(flushDragUpdate);
        }
        return;
      }

      if (columnId === 'rowNumber') return;
      const ci = colIdToIndexRef.current.get(columnId);
      if (ci === undefined || !dragAnchorRef.current) return;

      // Throttle via RAF: store latest target, flush once per frame
      pendingDragRef.current = { row: rowIndex, col: ci };
      if (!dragRafRef.current) {
        dragRafRef.current = requestAnimationFrame(flushDragUpdate);
      }
    },
    [flushDragUpdate],
  );

  const handleCellMouseUp = useCallback(() => {
    // Flush any pending drag update immediately
    if (dragRafRef.current) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = 0;
      flushDragUpdate();
    }
    isDraggingRef.current = false;
    rowDragRef.current = false;
    dragAnchorRef.current = null;
    pendingDragRef.current = null;
  }, [flushDragUpdate]);

  /* ── Context menu integration ── */

  const ensureCellSelectedForContextMenu = useCallback(
    (columnId: string, rowIndex: number) => {
      const ci = colIdToIndexRef.current.get(columnId);
      if (ci === undefined) return;

      if (!cacheRef.current.cells.has(cellKey(rowIndex, ci))) {
        // Clear all, select just this cell
        setSelection({
          activeCell: { row: rowIndex, col: ci },
          ranges: [makeRange(rowIndex, ci, rowIndex, ci)],
          selectionEnd: null,
          toggledCells: EMPTY_MAP,
        });
      }
    },
    [],
  );

  /* ── Lazy Set<string> for copy menus ── */

  const getSelectedCellsAsSet = useCallback((): Set<string> => {
    // Convert from internal "row:col" keys to "row:colId" keys for external use
    const ids = colIdsRef.current;
    const result = new Set<string>();
    for (const key of cacheRef.current.cells) {
      const sepIdx = key.indexOf(':');
      const row = parseInt(key.slice(0, sepIdx), 10);
      const col = parseInt(key.slice(sepIdx + 1), 10);
      if (col >= 0 && col < ids.length) {
        result.add(`${row}:${ids[col]}`);
      }
    }
    return result;
  }, []);

  const getSelectedCellCount = useCallback((): number => {
    return cacheRef.current.cells.size;
  }, []);

  /* ── Keyboard navigation ── */

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!tableFocusedRef.current) return;

      // Skip inputs
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      const isShift = e.shiftKey;
      const isCtrl = e.ctrlKey || e.metaKey;

      if (e.key === 'Escape') {
        setSelection(EMPTY_STATE);
        e.preventDefault();
        return;
      }

      // Ctrl+Alt+C: copy selected cells with headers
      if (isCtrl && e.altKey && e.code === 'KeyC') {
        e.preventDefault();
        onCopyWithHeadersRef.current?.();
        return;
      }

      // Ctrl+C: copy selected cells
      if (isCtrl && e.code === 'KeyC') {
        onCopyRef.current?.();
        return;
      }

      // Arrow keys (without Ctrl — Ctrl+Arrow is handled by table components for scroll-to-edge)
      if (isCtrl) return;

      let dr = 0;
      let dc = 0;
      switch (e.key) {
        case 'ArrowUp':
          dr = -1;
          break;
        case 'ArrowDown':
          dr = 1;
          break;
        case 'ArrowLeft':
          dc = -1;
          break;
        case 'ArrowRight':
          dc = 1;
          break;
        default:
          return;
      }

      e.preventDefault();

      setSelection((prev) => {
        const maxRow = rowCountRef.current - 1;
        const maxCol = colIdsRef.current.length - 1;
        if (maxRow < 0 || maxCol < 0) return prev;

        if (isShift) {
          // Shift+Arrow: extend selection
          const active = prev.activeCell ?? { row: 0, col: 0 };
          const end = prev.selectionEnd ?? { ...active };
          const newEnd: CellCoord = {
            row: Math.max(0, Math.min(maxRow, end.row + dr)),
            col: Math.max(0, Math.min(maxCol, end.col + dc)),
          };
          const newRange = makeRange(active.row, active.col, newEnd.row, newEnd.col);
          const newRanges = prev.ranges.length > 0 ? [...prev.ranges.slice(0, -1), newRange] : [newRange];
          return {
            activeCell: active,
            ranges: newRanges,
            selectionEnd: newEnd,
            toggledCells: EMPTY_MAP,
          };
        }

        // Plain arrow: move active, clear selection
        const active = prev.activeCell ?? { row: 0, col: 0 };
        const newActive: CellCoord = {
          row: Math.max(0, Math.min(maxRow, active.row + dr)),
          col: Math.max(0, Math.min(maxCol, active.col + dc)),
        };
        return {
          activeCell: newActive,
          ranges: [makeRange(newActive.row, newActive.col, newActive.row, newActive.col)],
          selectionEnd: null,
          toggledCells: EMPTY_MAP,
        };
      });

      // Scroll active cell into view (read latest activeCell from ref, no state updater side effect)
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const active = activeCellRef.current;
        if (!active) return;
        const hh = headerHeight;
        const visibleTop = el.scrollTop;
        const visibleBottom = visibleTop + el.clientHeight - hh;
        const activeTop = active.row * rowHeight;
        const activeBottom = activeTop + rowHeight;
        if (activeTop < visibleTop + hh) {
          el.scrollTop = activeTop - hh;
        } else if (activeBottom > visibleBottom + hh) {
          el.scrollTop = activeBottom - el.clientHeight + hh;
        }
      });
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [rowHeight, headerHeight, scrollContainerRef, rowCountRef]);

  /* ── Focus tracking + global mouseup ── */

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const tableEl = target.closest(tableSelector);
      tableFocusedRef.current = tableEl !== null;
    };

    const handleGlobalMouseUp = () => {
      // Flush any pending drag update
      if (dragRafRef.current) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = 0;
        flushDragUpdate();
      }
      isDraggingRef.current = false;
      rowDragRef.current = false;
      dragAnchorRef.current = null;
      pendingDragRef.current = null;
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [tableSelector, flushDragUpdate]);

  /* ── Auto-scroll during drag ── */

  useEffect(() => {
    const EDGE_PX = 50;
    const BASE_SPEED = 8;
    const UPDATE_INTERVAL = 60; // ms — throttle selection updates, scroll stays smooth
    let scrollRaf = 0;
    let prevRow = -1;
    let prevCol = -1;
    let lastColFromProbe = -1;
    let lastUpdateTime = 0;
    let hasPending = false;

    function flushPending() {
      if (!hasPending) return;
      hasPending = false;
      lastUpdateTime = performance.now();
      flushDragUpdate();
    }

    function tick() {
      scrollRaf = 0;
      if (!isDraggingRef.current || !lastMouseRef.current) return;

      const el = scrollContainerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const { clientX, clientY } = lastMouseRef.current;
      const hh = headerHeight;

      let dy = 0;
      let dx = 0;

      // Vertical edge detection (account for sticky header)
      const topEdge = rect.top + hh;
      if (clientY < topEdge + EDGE_PX) {
        dy = -Math.ceil(BASE_SPEED * Math.min((topEdge + EDGE_PX - clientY) / EDGE_PX, 3));
      } else if (clientY > rect.bottom - EDGE_PX) {
        dy = Math.ceil(BASE_SPEED * Math.min((clientY - rect.bottom + EDGE_PX) / EDGE_PX, 3));
      }

      // Horizontal edge detection
      if (clientX < rect.left + EDGE_PX) {
        dx = -Math.ceil(BASE_SPEED * Math.min((rect.left + EDGE_PX - clientX) / EDGE_PX, 3));
      } else if (clientX > rect.right - EDGE_PX) {
        dx = Math.ceil(BASE_SPEED * Math.min((clientX - rect.right + EDGE_PX) / EDGE_PX, 3));
      }

      if (dx === 0 && dy === 0) {
        // Leaving edge zone — flush any pending selection update
        flushPending();
        return;
      }

      el.scrollBy(dx, dy);

      // Compute target row from Y position
      const clampedY = Math.max(topEdge + 1, Math.min(rect.bottom - 1, clientY));
      const targetRow = Math.max(
        0,
        Math.min(
          rowCountRef.current - 1,
          Math.floor((el.scrollTop + clampedY - rect.top - hh) / rowHeight),
        ),
      );

      // Compute target column
      let targetCol: number;
      if (rowDragRef.current) {
        // Row drag mode: always span all columns
        targetCol = colIdsRef.current.length - 1;
      } else if (dx !== 0) {
        // Only probe DOM when scrolling horizontally
        const clampedX = Math.max(rect.left + 1, Math.min(rect.right - 1, clientX));
        const probe = document.elementFromPoint(clampedX, rect.top + hh / 2);
        if (probe) {
          const colEl = (probe as HTMLElement).closest('[data-column-id]');
          if (colEl) {
            const colId = colEl.getAttribute('data-column-id') ?? '';
            let idx = colIdToIndexRef.current.get(colId);
            if (idx === undefined && colId.startsWith('l2cp:')) {
              idx = colIdToIndexRef.current.get(colId.slice(5));
            }
            if (idx !== undefined) lastColFromProbe = idx;
          }
        }
        targetCol = lastColFromProbe >= 0
          ? lastColFromProbe
          : pendingDragRef.current?.col ?? dragAnchorRef.current?.col ?? -1;
      } else {
        targetCol = lastColFromProbe >= 0
          ? lastColFromProbe
          : pendingDragRef.current?.col ?? dragAnchorRef.current?.col ?? -1;
      }

      // Track target change, but throttle React updates
      if (targetCol >= 0 && (targetRow !== prevRow || targetCol !== prevCol)) {
        prevRow = targetRow;
        prevCol = targetCol;
        pendingDragRef.current = { row: targetRow, col: targetCol };
        hasPending = true;
      }

      if (hasPending && performance.now() - lastUpdateTime >= UPDATE_INTERVAL) {
        flushPending();
      }

      // Continue loop while in edge zone
      scrollRaf = requestAnimationFrame(tick);
    }

    function onMouseMove(e: MouseEvent) {
      if (!isDraggingRef.current) return;
      lastMouseRef.current = { clientX: e.clientX, clientY: e.clientY };
      if (!scrollRaf) scrollRaf = requestAnimationFrame(tick);
    }

    document.addEventListener('mousemove', onMouseMove);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
    };
  }, [headerHeight, rowHeight, scrollContainerRef, flushDragUpdate, rowCountRef]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (dragRafRef.current) cancelAnimationFrame(dragRafRef.current);
    };
  }, []);

  return {
    selection,
    isCellSelected,
    isCellActive,
    isRowSelected,
    isColSelected,
    getCellBorders,
    colIdToIndex,
    clearSelection,
    selectColumn,
    deselectColumn,
    isColumnFullySelected,
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,
    ensureCellSelectedForContextMenu,
    getSelectedCellsAsSet,
    getSelectedCellCount,
  };
}
